import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDAvatar from 'components/MDAvatar';
import { AdviserSelection } from './AdviserSelection';
import { AdviserDetails } from './AdviserDetails';
import { getTemplateHtml } from '../api/contract_templates';

/**
 * InitialStep component for the first step in document generation.
 * 
 * @param {Object} props - Component props
 * @param {string} props.templateName - Name of the template to generate
 * @param {string} props.clientName - Name of the client
 * @param {string} props.clientType - Type of client ('individual' or 'entity')
 * @param {Array} props.advisers - List of available advisers
 * @param {string} [props.selectedAdviser] - Currently selected adviser ID
 * @param {boolean} props.advisersLoading - Loading state for advisers
 * @param {string} [props.advisersError] - Error message for advisers
 * @param {Object} [props.adviserDetails] - Details of the selected adviser
 * @param {boolean} props.adviserDetailsLoading - Loading state for adviser details
 * @param {string} [props.adviserDetailsError] - Error message for adviser details
 * @param {Function} props.onAdviserChange - Callback when adviser selection changes
 * @param {Function} props.onGenerateDocument - Callback to generate document
 * @param {Function} [props.onHtmlReady] - Callback with HTML after generation
 */
export const InitialStep = ({
  templateName,
  clientName,
  clientType,
  advisers,
  selectedAdviser,
  advisersLoading,
  advisersError,
  adviserDetails,
  adviserDetailsLoading,
  adviserDetailsError,
  onAdviserChange,
  onGenerateDocument,
  onHtmlReady,
}) => {
  const handleGenerateDocument = async () => {
    await onGenerateDocument();
    // Optionally fetch HTML after document generation
    if (onHtmlReady && templateName && clientName && clientType && selectedAdviser) {
      try {
        const html = await getTemplateHtml(templateName, clientName, clientType, selectedAdviser);
        onHtmlReady(html);
      } catch (err) {
        // Optionally handle error
        // eslint-disable-next-line no-console
        console.error('Failed to fetch template HTML:', err);
      }
    }
  };

  return (
    <MDBox display="flex" flexDirection="column" alignItems="center" py={8}>
      <MDBox display="flex" alignItems="center" gap={2} mb={3}>
        <MDAvatar bgColor="info" size="sm" sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>1</MDAvatar>
        <MDTypography variant="h5" fontWeight="medium" textAlign="center">
          Ready to Generate Document
        </MDTypography>
      </MDBox>
      <MDBox 
        sx={{ 
          backgroundColor: 'grey.50', 
          borderRadius: 2, 
          p: 3, 
          mb: 4,
          border: '1px solid',
          borderColor: 'grey.200',
          maxWidth: 500,
          width: '100%'
        }}
      >
        <MDTypography variant="body1" color="text.secondary" textAlign="center" mb={1}>
          <strong>Template:</strong> {templateName}
        </MDTypography>
        <MDTypography variant="body1" color="text.secondary" textAlign="center">
          <strong>Client:</strong> {clientName} ({clientType})
        </MDTypography>
      </MDBox>
      
      <AdviserSelection
        advisers={advisers}
        selectedAdviser={selectedAdviser}
        advisersLoading={advisersLoading}
        advisersError={advisersError}
        onAdviserChange={onAdviserChange}
      />
      
      <AdviserDetails
        adviserDetails={adviserDetails}
        loading={adviserDetailsLoading}
        error={adviserDetailsError}
      />
      
      <MDButton
        variant="gradient"
        color="success"
        size="large"
        onClick={handleGenerateDocument}
        startIcon={<Icon>play_arrow</Icon>}
        sx={{ 
          borderRadius: '25px', 
          px: 6, 
          py: 1.5,
          fontSize: '1.1rem',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          }
        }}
        disabled={!selectedAdviser}
        aria-label="Generate document"
      >
        Generate Document
      </MDButton>
    </MDBox>
  );
};

InitialStep.propTypes = {
  templateName: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  clientType: PropTypes.string.isRequired,
  advisers: PropTypes.array.isRequired,
  selectedAdviser: PropTypes.string,
  advisersLoading: PropTypes.bool.isRequired,
  advisersError: PropTypes.string,
  adviserDetails: PropTypes.object,
  adviserDetailsLoading: PropTypes.bool.isRequired,
  adviserDetailsError: PropTypes.string,
  onAdviserChange: PropTypes.func.isRequired,
  onGenerateDocument: PropTypes.func.isRequired,
  onHtmlReady: PropTypes.func,
}; 